import { isSupportedSymbol, SUPPORTED_CHAIN_IDS } from '@frontend/common';
import {
  chainIdToShortName,
  resolveChainIdOrShortName,
} from '@frontend/utils/chains';
import { stringUtils } from '@frontend/utils/string';
import { TokenInfo } from '@yodlpay/tokenlists';
import { z } from 'zod';

// ============================================================================
// Types and Constants
// ============================================================================

/**
 * Special case mappings for token symbols with non-standard casing
 */
const TOKEN_SYMBOL_CASE_MAP: Record<string, string> = {
  EURE: 'EURe',
  CRVUSD: 'crvUSD',
  XDAI: 'xDAI',
  WXDAI: 'wxDAI',
  XAUT: 'XAUt',
} as const;

// ============================================================================
// Base Schema Components
// ============================================================================

/**
 * Base schema for token configuration (supports arrays and comma-separated strings)
 */
const BaseTokenConfigSchema = z.preprocess((input: unknown) => {
  if (Array.isArray(input)) return input;
  if (typeof input === 'string') {
    return stringUtils.commaStringToArray(input);
  }
  return input;
}, z.array(z.string()));

/**
 * Base schema for chain configuration (supports arrays and comma-separated strings)
 */
const BaseChainConfigSchema = z.preprocess(
  (input: unknown) => {
    if (Array.isArray(input)) return input;
    if (typeof input === 'string') {
      return stringUtils.commaStringToArray(input);
    }
    return input;
  },
  z.array(z.union([z.string(), z.number()])).transform((chains) =>
    chains
      .map((chain) => {
        if (typeof chain === 'string') {
          return resolveChainIdOrShortName(chain);
        }
        return chain;
      })
      .filter(
        (id): id is number =>
          id !== undefined &&
          id !== 0 &&
          SUPPORTED_CHAIN_IDS.includes(
            id as (typeof SUPPORTED_CHAIN_IDS)[number],
          ),
      ),
  ),
);

// ============================================================================
// Main Schemas
// ============================================================================

/**
 * Known field names that should be excluded from otherProps
 */
const KNOWN_FIELDS = [
  'chains',
  'chainIds',
  'tokens',
  'tokenSymbols',
  'redirectUrl',
  'currency',
  'amount',
  'webhooks',
  'og',
  'memo',
  'urlTokenIn',
  'autoRedirect',
] as const;

/**
 * Creates a standardized PaymentConfig object from various input sources.
 * Ensures consistent output structure regardless of input source (URL params, JSON, etc.)
 */
const createPaymentConfig = (data: any) => {
  const result = {
    // Merge legacy and new field formats - new fields take precedence
    chainIds: data.chains ?? data.chainIds ?? [],
    tokenSymbols: data.tokens ?? data.tokenSymbols ?? [],

    // Optional configuration fields (only include if defined)
    ...(data.redirectUrl && { redirectUrl: data.redirectUrl }),
    ...(data.currency && { currency: data.currency }),
    ...(data.amount !== undefined && { amount: data.amount }),
    ...(data.webhooks && { webhooks: data.webhooks }),
    ...(data.og && { og: data.og }),
    ...(data.memo && { memo: data.memo }),
    ...(data.urlTokenIn && { urlTokenIn: data.urlTokenIn }),
    ...(data.autoRedirect !== undefined && { autoRedirect: data.autoRedirect }),

    // Preserve any additional properties (excluding known fields)
    ...Object.fromEntries(
      Object.entries(data).filter(
        ([key]) => !KNOWN_FIELDS.includes(key as any),
      ),
    ),
  };

  return result;
};

/**
 * Main schema for validating user or application preferences.
 *
 * Features:
 * - JSON string preprocessing with error handling
 * - Legacy field support (chainIds, tokenSymbols) with new field precedence
 * - Comprehensive validation for all preference types
 * - Extensible via catchall for unknown properties
 *
 * @example
 * ```typescript
 * const prefs = PreferencesSchema.parse({
 *   chains: "eth,arb",
 *   tokens: ["USDC", "USDT"],
 *   redirectUrl: "https://example.com"
 * });
 * ```
 */
export const PreferencesSchema = z.preprocess(
  (input) => {
    try {
      if (typeof input === 'string' && input.trim() !== '') {
        return JSON.parse(input);
      }
      return input;
    } catch (error) {
      console.error('Failed to parse preferences JSON:', error);
      return {};
    }
  },
  z
    .object({
      // Legacy fields (backward compatibility)
      /** @deprecated Use `chains` instead */
      chainIds: BaseChainConfigSchema.optional(),
      /** @deprecated Use `tokens` instead */
      tokenSymbols: BaseTokenConfigSchema.optional(),

      // Preferred fields
      /** Chain IDs/names as array or comma-separated string */
      chains: BaseChainConfigSchema.optional(),
      /** Token symbols as array or comma-separated string */
      tokens: BaseTokenConfigSchema.optional(),

      // Configuration fields
      /** Redirect URL (must be valid URL) */
      redirectUrl: z.string().url().optional(),
      /** Currency code (e.g., "USD", "EUR") */
      currency: z.string().optional(),
      /** Default amount (non-negative number) */
      amount: z.number().nonnegative().optional(),
      /** Webhook URLs for notifications */
      webhooks: z.array(z.string().url()).optional(),
      /** Open Graph settings for social media previews */
      og: z
        .object({
          baseUrl: z.string().url().optional(),
        })
        .optional(),
      /** Payment memo/description */
      memo: z.string().optional(),
      /** Parsed token information for URL parameter */
      urlTokenIn: z.custom<TokenInfo>().optional(),
      /** Auto-redirect flag */
      autoRedirect: z.boolean().optional(),
    })
    .catchall(z.unknown())
    .transform(createPaymentConfig),
);

// ============================================================================
// Export Utilities
// ============================================================================

/**
 * Parses token parameters with special case handling for symbol variations.
 * Exported for backward compatibility.
 */
export const tokenParamToSymbols = (
  tokens?: string | string[] | null,
): string[] | undefined => {
  if (!tokens) return undefined;

  const tokenArray = stringUtils.normalizeToArray(tokens);

  return tokenArray
    .map((symbol) => {
      const upperSymbol = symbol.toUpperCase();
      return TOKEN_SYMBOL_CASE_MAP[upperSymbol] || upperSymbol;
    })
    .filter(isSupportedSymbol);
};

/**
 * Converts preferences object to text format for storage (e.g., ENS records).
 *
 * Transforms:
 * - chainIds → comma-separated chain short names
 * - tokenSymbols → comma-separated token symbols
 * - Preserves other properties as-is
 *
 * @param preferences - Preferences object with chainIds and tokenSymbols
 * @returns JSON string representation
 *
 * @example
 * ```typescript
 * const text = convertPreferencesToText({
 *   chainIds: [1, 42161],
 *   tokenSymbols: ["USDC", "USDT"],
 *   redirectUrl: "https://example.com"
 * });
 * // Returns: '{"tokens":"USDC,USDT","chains":"eth,arb","redirectUrl":"https://example.com"}'
 * ```
 */
export const convertPreferencesToText = (preferences: any): string => {
  // Convert chain IDs to short names
  const processedChains = preferences.chainIds
    ?.map((chain: number) => chainIdToShortName(chain))
    .filter(Boolean);

  // Build formatted object
  const formattedPreferences: Record<string, any> = {
    tokens: preferences.tokenSymbols
      ? stringUtils.arrayToCommaString(preferences.tokenSymbols)
      : undefined,
    chains:
      processedChains && processedChains.length > 0
        ? stringUtils.arrayToCommaString(processedChains)
        : undefined,
  };

  // Add other properties (excluding internal fields)
  for (const key in preferences) {
    if (key !== 'chainIds' && key !== 'tokenSymbols') {
      formattedPreferences[key] = preferences[key];
    }
  }

  // Remove undefined values
  Object.keys(formattedPreferences).forEach((key) => {
    if (formattedPreferences[key] === undefined) {
      delete formattedPreferences[key];
    }
  });

  return JSON.stringify(formattedPreferences);
};

// ============================================================================
// Inferred Types
// ============================================================================

export type PaymentConfig = z.infer<typeof PreferencesSchema>;
